import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
function calculateAge(dob) { if (!dob) return 0; const birthDate = new Date(dob); const today = new Date(); let age = today.getFullYear() - birthDate.getFullYear(); const m = today.getMonth() - birthDate.getMonth(); if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) { age--; } return age; }
import DashboardCore from "../../shared/dashboard/DashboardCore";
import MoodTracker from "../../components/Dashboard/MoodTracker";
import MoodGraph from "../../components/Dashboard/MoodGraph";
import HealingTree from "../../components/Dashboard/HealingTree";
import ExploreProviders from "../../components/Dashboard/ExploreProviders";
import RecentChats from "../../components/Dashboard/RecentChats";
import SavedArticles from "../../components/Dashboard/SavedArticles";
import LiveQnA from "../../components/Dashboard/LiveQnA";
import SupportHelpline from "../../components/Dashboard/SupportHelpline";
import VentBox from "../../components/Sanctuary/VentBox";
import DigitalDiary from "../../components/Sanctuary/DigitalDiary";
import { Card, CardHeader, CardContent } from "../../components/ui/card";
import BreathingSphere from "../../components/Sanctuary/BreathingSphere";
import SanctuaryWallModal from "../../components/Sanctuary/SanctuaryWallModal";
import { Flame, Sprout, HeartHandshake, Book, Wind, MessageSquareText, Sparkles } from "lucide-react";

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
};

export default function DashboardSeeker() {
    const { user } = useAuth();
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [showVent, setShowVent] = useState(false);
    const [selectedMood, setSelectedMood] = useState(null);
    const [moodRefreshTrigger, setMoodRefreshTrigger] = useState(0);
    const [showDiary, setShowDiary] = useState(false);
    const [showBreathing, setShowBreathing] = useState(false);
    const [showWall, setShowWall] = useState(false);

    const guardAction = (callback) => {
        if (user?.isGuest) {
            setShowWall(true);
        } else {
            callback();
        }
    };

    const handleMoodSelect = (mood) => {
        setSelectedMood(mood);
        setMoodRefreshTrigger((prev) => prev + 1);
    };

    useEffect(() => {
        if (user && !user.seeker) {
            navigate("/signup-flow", { replace: true });
        }
    }, [user, navigate]);

    return (
        <DashboardCore role="SEEKER">
            <div className="space-y-8">
                {/* Guest Session Nudge */}
                {!user && (
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-6 rounded-3xl bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6 overflow-hidden relative group"
                    >
                        <div className="absolute top-0 right-0 p-12 -mr-12 -mt-12 bg-white/10 rounded-full blur-3xl group-hover:scale-110 transition-transform duration-1000" />
                        <div className="flex items-center gap-4 relative z-10">
                            <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-md">
                                <Sparkles className="w-6 h-6 text-blue-100" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold">{t('dash_guest_title')}</h3>
                                <p className="text-blue-100 text-sm font-light opacity-90">{t('dash_guest_desc')}</p>
                            </div>
                        </div>
                        <button 
                            onClick={() => navigate('/signup')}
                            className="whitespace-nowrap px-8 py-3 bg-white text-blue-700 rounded-full font-bold text-sm hover:bg-blue-50 transition-all shadow-lg active:scale-95 relative z-10"
                        >
                            {t('dash_guest_cta')}
                        </button>
                    </motion.div>
                )}

                {/* Section 1: The Inner Self (Mood & Growth) */}
                <motion.section variants={itemVariants}>
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <h2 className="text-xl font-display font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                                <Sprout className="w-6 h-6 text-brand-green" /> {t('dash_inner_garden')}
                            </h2>
                        </div>
                        <button
                            onClick={() => setShowVent(true)}
                            className="flex items-center gap-2 px-3 py-1.5 bg-orange-50 dark:bg-orange-900/10 text-orange-600 dark:text-orange-400 rounded-lg text-sm font-medium hover:bg-orange-100 dark:hover:bg-orange-900/20 transition-colors"
                        >
                            <Flame className="w-4 h-4" />
                            <span>{t('dash_burn_worry')}</span>
                        </button>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-1 space-y-6">
                            <MoodTracker onMoodSelect={handleMoodSelect} />

                            <AnimatePresence>
                                {selectedMood && (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="p-5 rounded-2xl border border-indigo-100 dark:border-indigo-900 bg-white/50 dark:bg-indigo-900/5 backdrop-blur-md shadow-sm"
                                    >
                                        <div className="flex items-center gap-2 mb-3">
                                            <div className="p-1.5 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg">
                                                <Sparkles className="w-4 h-4 text-indigo-500" />
                                            </div>
                                            <h3 className="text-[13px] font-bold text-gray-800 dark:text-gray-200">
                                                {t('dash_nurture_tips')}
                                            </h3>
                                        </div>

                                        {selectedMood === 'Happy' && (
                                            <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                                                <p className="text-xs text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                                                    {t('dash_nurture_happy')}
                                                </p>
                                                <button
                                                    onClick={() => guardAction(() => setShowDiary(true))}
                                                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-md shadow-indigo-200 dark:shadow-none transition-all active:scale-95"
                                                >
                                                    <Book className="w-3.5 h-3.5" /> {t('dash_diary_cta')}
                                                </button>
                                            </div>
                                        )}

                                        {selectedMood === 'Sad' && (
                                            <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                                                <p className="text-xs text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                                                    {t('dash_nurture_sad')}
                                                </p>
                                                <button
                                                    onClick={() => setShowVent(true)}
                                                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-xs font-bold shadow-md shadow-orange-200 dark:shadow-none transition-all active:scale-95"
                                                >
                                                    <Flame className="w-3.5 h-3.5" /> {t('dash_burn_worry')}
                                                </button>
                                            </div>
                                        )}

                                        {selectedMood === 'Neutral' && (
                                            <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                                                <p className="text-xs text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                                                    {t('dash_nurture_neutral')}
                                                </p>
                                                <button
                                                    onClick={() => setShowBreathing(true)}
                                                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl text-xs font-bold shadow-md shadow-teal-200 dark:shadow-none transition-all active:scale-95"
                                                >
                                                    <Wind className="w-3.5 h-3.5" /> {t('dash_start_breathing')}
                                                </button>
                                            </div>
                                        )}

                                        {!['Happy', 'Sad', 'Neutral'].includes(selectedMood) && (
                                            <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
                                                <p className="text-xs text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                                                    {t('dash_nurture_other')}
                                                </p>
                                                <button
                                                    onClick={() => guardAction(() => navigate('/chat'))}
                                                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-brand-blue hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md shadow-blue-200 dark:shadow-none transition-all active:scale-95"
                                                >
                                                    <MessageSquareText className="w-3.5 h-3.5" /> {t('dash_connect_guide')}
                                                </button>
                                            </div>
                                        )}
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <MoodGraph refreshTrigger={moodRefreshTrigger} />
                        </div>
                        {/* 
                        <div className="lg:col-span-2">
                            <HealingTree />
                        </div>
                        */}
                    </div>
                </motion.section>

                {/* Vent Box Modal */}
                {showVent && <VentBox onClose={() => setShowVent(false)} />}
                {showDiary && <DigitalDiary onClose={() => setShowDiary(false)} />}
                {showBreathing && <BreathingSphere onClose={() => setShowBreathing(false)} />}
                <SanctuaryWallModal isOpen={showWall} onClose={() => setShowWall(false)} />

                {/* Section 2: Connection & Guidance */}
                <motion.section variants={itemVariants}>
                    <div className="flex items-center gap-2 mb-4">
                        <h2 className="text-xl font-display font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                            <HeartHandshake className="w-6 h-6 text-brand-blue" /> {t('dash_support_circle')}
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        <div className="md:col-span-2">
                            <Card className="h-full border-blue-100 dark:border-blue-900 bg-blue-50/50 dark:bg-blue-900/10">
                                <CardHeader>
                                    <h3 className="font-semibold text-blue-900 dark:text-blue-100">{t('dash_find_guide')}</h3>
                                </CardHeader>
                                <CardContent>
                                    <ExploreProviders />
                                </CardContent>
                            </Card>
                        </div>
                        <div>
                            <Card className="h-full">
                                <CardContent className="p-0 h-full">
                                    <RecentChats />
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </motion.section>

                {/* Section 3: Resources & Community */}
                <motion.section variants={itemVariants}>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {/* 
                        <Card className="lg:col-span-2">
                            <CardHeader><h3 className="font-semibold">My Library</h3></CardHeader>
                            <CardContent><SavedArticles /></CardContent>
                        </Card>
                        */}
                        {/* 
                        <Card>
                            <CardHeader><h3 className="font-semibold">Live Q&A</h3></CardHeader>
                            <CardContent><LiveQnA /></CardContent>
                        </Card>
                        */}
                        {/* Reach out to us */}
                        <Card className="h-full border-none shadow-xl shadow-brand-blue/5 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm overflow-hidden">
                            <CardContent className="p-0"><SupportHelpline /></CardContent>
                        </Card>
                    </div>
                </motion.section>
            </div>
        </DashboardCore>
    );
}
